import { supabase } from "@/integrations/supabase/client";

export type LogLevel = "info" | "warning" | "error" | "critical";
export type LogCategory = "auth" | "user" | "payment" | "system" | "api" | "integration" | "luma" | "other";

interface LogInput {
  level?: LogLevel;
  category?: LogCategory;
  event: string;
  message?: string;
  payload?: Record<string, unknown>;
  userId?: string | null;
  userEmail?: string | null;
}

// Mask sensitive fields in payloads to keep logs safe.
const SENSITIVE_KEYS = /password|token|secret|apikey|api_key|authorization|cpf|cnpj|pix|card/i;
function sanitize(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (depth > 4) return "[Truncated]";
  if (Array.isArray(value)) return value.slice(0, 20).map((v) => sanitize(v, depth + 1));
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (SENSITIVE_KEYS.test(k)) {
        out[k] = "[REDACTED]";
      } else {
        out[k] = sanitize(v, depth + 1);
      }
    }
    return out;
  }
  if (typeof value === "string" && value.length > 1000) return value.slice(0, 1000) + "…";
  return value;
}

export async function logEvent(input: LogInput): Promise<void> {
  try {
    let userId = input.userId ?? null;
    let userEmail = input.userEmail ?? null;
    if (userId === null) {
      const { data } = await supabase.auth.getUser();
      userId = data.user?.id ?? null;
      userEmail = data.user?.email ?? null;
    }

    await supabase.from("system_logs").insert([{
      level: input.level ?? "info",
      source: "frontend",
      category: input.category ?? "other",
      event: input.event,
      message: input.message ?? "",
      user_id: userId,
      user_email: userEmail,
      payload: sanitize(input.payload ?? {}) as any,
    }]);
  } catch (err) {
    // Never let logging break the app
    console.warn("[systemLogger] failed to record log", err);
  }
}

// Convenience helpers
export const logInfo = (event: string, message?: string, payload?: Record<string, unknown>, category: LogCategory = "other") =>
  logEvent({ level: "info", category, event, message, payload });

export const logWarning = (event: string, message?: string, payload?: Record<string, unknown>, category: LogCategory = "other") =>
  logEvent({ level: "warning", category, event, message, payload });

export const logError = (event: string, message?: string, payload?: Record<string, unknown>, category: LogCategory = "other") =>
  logEvent({ level: "error", category, event, message, payload });

export const logCritical = (event: string, message?: string, payload?: Record<string, unknown>, category: LogCategory = "other") =>
  logEvent({ level: "critical", category, event, message, payload });

// Install global handlers once (browser)
let installed = false;
export function installGlobalErrorLogging() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (e) => {
    logError(
      "frontend_uncaught_error",
      e.message || "Uncaught error",
      {
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        stack: e.error?.stack,
      },
      "system",
    );
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    const message = reason?.message ?? String(reason);
    logError(
      "frontend_unhandled_rejection",
      message,
      { stack: reason?.stack },
      "system",
    );
  });
}
